import { useCallback, useEffect, useState } from 'react';

const CONSIDERATION_MAX_LENGTH = 2000;

const clamp = (value: string): string => value.slice(0, CONSIDERATION_MAX_LENGTH);

export interface UseBidderConsiderationDraftResult {
  text: string;
  setText: (value: string) => void;
  isTextareaFocused: boolean;
  setIsTextareaFocused: (v: boolean) => void;
  isEditingMine: boolean;
  hasSavedText: boolean;
  isDirty: boolean;
  showSavedView: boolean;
  trimmedText: string;
  startEditMine: () => void;
  handleCancelEdit: () => void;
  maxLength: number;
}

export function useBidderConsiderationDraft(consideration: string): UseBidderConsiderationDraftResult {
  const [text, setTextState] = useState(() => clamp(consideration || ''));
  const [isEditingMine, setIsEditingMine] = useState(() => !consideration?.trim());
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);

  useEffect(() => {
    setTextState(clamp(consideration || ''));
    if (consideration?.trim()) {
      setIsEditingMine(false);
    } else {
      setIsEditingMine(true);
    }
  }, [consideration]);

  const setText = useCallback((value: string) => {
    setTextState(clamp(value));
  }, []);

  const trimmedText = text.trim();
  const savedTrimmed = (consideration || '').trim();
  const hasSavedText = Boolean(savedTrimmed);
  const isDirty = trimmedText !== savedTrimmed;
  const showSavedView = hasSavedText && !isEditingMine;

  const startEditMine = useCallback(() => {
    setTextState(clamp(consideration || ''));
    setIsEditingMine(true);
  }, [consideration]);

  const handleCancelEdit = useCallback(() => {
    setTextState(clamp(consideration || ''));
    setIsEditingMine(false);
  }, [consideration]);

  return {
    text,
    setText,
    isTextareaFocused,
    setIsTextareaFocused,
    isEditingMine,
    hasSavedText,
    isDirty,
    showSavedView,
    trimmedText,
    startEditMine,
    handleCancelEdit,
    maxLength: CONSIDERATION_MAX_LENGTH,
  };
}
